import React, { type JSX } from "react";

import { connect } from "react-redux";
import { setDialogIsOpen } from "./DashboardCopyToUserDialogActions";
import { ProModal } from "pro-features/components/Modals/src/ProModal";
import { Autocomplete } from "@similarweb/ui-components";
import { DotsLoader } from "@similarweb/ui-components";
import { ListItemUser } from "@similarweb/ui-components";
import ImpersonateAuthModal from "app/components/React/ImpersonateAuth/ImpersonateAuthModal";
import ImpersonateAuthCtrl from "app/components/React/ImpersonateAuth/ImpersonateAuthCtrl";
import { Header } from "pro-features/components/Dashboard/SharedDashboardModal";
import I18n from "pro-features/components/WithTranslation/src/I18n";
import { DefaultFetchService } from "app/services/fetchService";
import { duplicateDashboardTo } from "app/user-data/UserResource";
import type { IDashboardNavItem } from "app/components/SecondaryBar/NavBars/DashboardsNavBar/DashboardNavBarTypes";
import { showSuccessToast } from "app/actions/toast_actions";
import type { RootState, ThunkDispatchCommon } from "single-spa/store/types";

type User = {
    UserName: string;
    Id: number;
    AccountName: string;
};

const fetchService = DefaultFetchService.getInstance();

interface IDashboardCopyToUserDialogProps {
    isOpen: boolean;
    setDialogIsOpen: (isOpen: boolean) => void;
    selectedDashboard: IDashboardNavItem;
    showCopyToSuccess: (message: string) => void;
}
const DashboardCopyToUserDialog: React.FC<IDashboardCopyToUserDialogProps> = ({
    isOpen,
    setDialogIsOpen,
    selectedDashboard,
    showCopyToSuccess,
}) => {
    const [showImpersonateAuth, setShowImpersonateAuth] = React.useState(false);
    const [showAutocomplete, setShowAutocomplete] = React.useState(false);
    const onClose = () => {
        setShowAutocomplete(false);
        setDialogIsOpen(false);
    };
    const onClickUser = (user: User) => async () => {
        const { Id: userId, UserName } = user;
        const { id: dashbordId, title: dashboardTitle } = selectedDashboard;
        const newDashboardTitle = `Copy of ${dashboardTitle}`;
        await duplicateDashboardTo({
            Id: dashbordId,
            Name: newDashboardTitle,
            UserId: userId.toString(),
        });
        showCopyToSuccess(`Copy of "${dashboardTitle}" to ${UserName} is done`);
        // setShowAutocomplete(false);
        // setDialogIsOpen(false);
    };
    const getListItems = async (query: string) => {
        const url = `/useridentity/getidentities?action=DuplicateDashboardToOthers&token=${query.trim()}`;
        const { Result: users } = await fetchService.get<{
            Result: User[];
        }>(url);
        return users.slice(0, 10).map((user) => {
            const { UserName, Id, AccountName } = user;
            return (
                <ListItemUser
                    key={Id.toString()}
                    email={AccountName}
                    name={UserName}
                    onClick={onClickUser(user)}
                />
            );
        });
    };
    const onImpersonateVerification = () => {
        setShowImpersonateAuth(false);
    };
    const onImpersonateClose = () => {
        setShowImpersonateAuth(false);
        setDialogIsOpen(false);
    };
    React.useEffect(() => {
        if (isOpen) {
            setShowImpersonateAuth(true);
            ImpersonateAuthCtrl.shouldAuthenticate()
                .then((res) => {
                    if (!res) {
                        setShowImpersonateAuth(false);
                        setShowAutocomplete(true);
                    }
                })
                .catch((err) => console.log(err));
        } else {
            setShowImpersonateAuth(false);
        }
    }, [isOpen]);
    const proModalProps = {
        customStyles: {
            content: {
                padding: "24px",
                width: "580px",
            },
        },
    };
    return (
        <>
            <ProModal
                isOpen={showAutocomplete}
                onCloseClick={onClose}
                {...proModalProps}
            >
                <Header>
                    <I18n>Usermanagement.Modal.Duplicate.Dashboard.Title</I18n>
                </Header>

                <Autocomplete
                    getListItems={getListItems}
                    loadingComponent={<DotsLoader />}
                    debounce={400}
                />
            </ProModal>
            {showImpersonateAuth ? (
                <ImpersonateAuthModal
                    onVerify={onImpersonateVerification}
                    onClose={onImpersonateClose}
                />
            ) : null}
        </>
    );
};
const mapStateToProps = (store: RootState) => {
    const { isOpen, selectedDashboard } = store.customDashboard.dashboardCopyToUserDialog;
    return {
        isOpen,
        selectedDashboard,
    };
};

const mapDispatchToProps = (dispatch: ThunkDispatchCommon) => {
    return {
        setDialogIsOpen: (isOpen = false) => {
            dispatch(setDialogIsOpen(isOpen));
        },
        showCopyToSuccess: (message: string | JSX.Element) => {
            dispatch(showSuccessToast(message));
        },
    };
};

const connected = connect(mapStateToProps, mapDispatchToProps)(DashboardCopyToUserDialog);

export default connected;
